import React, { useContext } from 'react'
import CssBaseline from '@material-ui/core/CssBaseline'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import Paper from '@material-ui/core/Paper'
import Grid, { GridSpacing } from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import CardActions from '@material-ui/core/CardActions'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import DoneIcon from '@material-ui/icons/Done'
import { useMobileOrientation } from 'react-device-detect'
import {
  PaletteType,
  unstable_createMuiStrictModeTheme as createMuiTheme
} from '@material-ui/core'

import Form, {
  DynamicCardAction,
  FormSection,
  FormSectionTypes,
  IconTypes
} from './interfaces/Form'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import FormControl from '@material-ui/core/FormControl'
import { ThemeProvider } from '@material-ui/core'
import DynamicFields from './DynamicFields'
import DynamicFormContext from './provider/DynamicFormContext'
import CloudDoneOutlinedIcon from '@material-ui/icons/CloudDoneOutlined'
import CircularProgress from '@material-ui/core/CircularProgress'
import Tooltip from '@material-ui/core/Tooltip'
import Button from '@material-ui/core/Button'
import MUITheme from './theme'
import { useIsSmallScreen, useUneeqSpaceToTalk } from 'uneeq-react-core'

interface DynamicFormProps {
  schema: Form
  paletteType: PaletteType
  showTitle: boolean
  useStyles: any
  gridSpacing?: GridSpacing
  fieldSuggestedResponses?: any
  sendText: (utterance: string) => void
}

const DynamicForm: React.FC<DynamicFormProps> = ({
  schema,
  paletteType = 'dark',
  showTitle = true,
  useStyles,
  gridSpacing,
  fieldSuggestedResponses,
  sendText
}) => {
  const classes = useStyles()
  const [entityQuantity, updateEntityQuantity] = React.useState(1)
  const { formData, setFormData } = useContext(DynamicFormContext)
  const { sending } = useUneeqSpaceToTalk()
  const isSmallScreen = useIsSmallScreen()
  const { isLandscape } = useMobileOrientation()
  // Example usage for form components here: https://github.com/mui-org/material-ui/tree/master/docs/src/pages/getting-started/templates/checkout

  // TODO: parse FormSchema cards and construct Paper components to establish key sections of form

  const theme = createMuiTheme(MUITheme)
  theme.palette.type = paletteType

  const getButtonIcon = (name: string) => {
    switch (name) {
      case IconTypes.Delete:
        return <DeleteIcon />
      case IconTypes.Add:
        return <AddIcon />
      case IconTypes.Done:
        return <DoneIcon />
      case IconTypes.CircularProgress:
        return <CircularProgress color="primary" />
      default:
        return ''
    }
  }

  const actionButtonHandler = (action: DynamicCardAction) => {
    if (
      action.submitFieldAsUtterance &&
      formData[action.submitFieldAsUtterance]
    ) {
      sendText(formData[action.submitFieldAsUtterance])
    } else if (!action.submitFieldAsUtterance) {
      sendText(action.utterance)
    }
  }

  return (
    <React.Fragment>
      <CssBaseline />
      <ThemeProvider theme={theme}>
        <DynamicFormContext.Consumer>
          {context => (
            <React.Fragment>
              {showTitle && (
                <AppBar
                  position="absolute"
                  color="default"
                  className={classes.appBar}
                >
                  <Toolbar className={classes.toolbar}>
                    <Typography variant="h6" color="inherit" noWrap>
                      {schema.title}
                    </Typography>
                    {context.saved ? (
                      <Tooltip
                        title="Changes saved to the cloud"
                        aria-label="add"
                      >
                        <CloudDoneOutlinedIcon />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Saving changes..." aria-label="add">
                        <CircularProgress size={16} />
                      </Tooltip>
                    )}
                  </Toolbar>
                </AppBar>
              )}
              {context.hasFormData ? (
                <main className={classes.layout}>
                  {schema.layout.map((section: FormSection) => {
                    return (
                      <Paper
                        elevation={0}
                        key={section.key}
                        className={classes.paper}
                      >
                        {section.label && (
                          <Typography
                            component="h1"
                            variant="h6"
                            align="center"
                          >
                            {section.label}
                          </Typography>
                        )}

                        {section.helperText && (
                          <Typography variant="body1" paragraph align="center">
                            {section.helperText}
                          </Typography>
                        )}

                        {section.type === FormSectionTypes.MultiCard &&
                        section.fields &&
                        section.fields.length > 0 ? (
                          <Grid>
                            {[...Array(entityQuantity)].map((_, index) => (
                              <Card
                                key={`${section.key}-${index}`}
                                variant="outlined"
                                id={`entity-${index}`}
                              >
                                <CardContent>
                                  <DynamicFields
                                    fields={section.fields}
                                    index={index}
                                    gridSpacing={gridSpacing}
                                    useStyles={useStyles}
                                    fieldSuggestedResponses={
                                      fieldSuggestedResponses
                                    }
                                    theme={theme}
                                  />
                                </CardContent>

                                <CardActions>
                                  <IconButton aria-label="delete">
                                    <DeleteIcon />
                                  </IconButton>
                                </CardActions>
                              </Card>
                            ))}
                            <IconButton aria-label="add">
                              <AddIcon />
                            </IconButton>{' '}
                            Add new entity
                          </Grid>
                        ) : (
                          <FormControl
                            component="fieldset"
                            fullWidth={true}
                            className={classes.formSection}
                            style={{
                              flexDirection:
                                isSmallScreen && !isLandscape
                                  ? 'column'
                                  : section.direction || 'column'
                            }}
                          >
                            {section.fields ? (
                              <DynamicFields
                                fields={section.fields}
                                gridSpacing={gridSpacing}
                                useStyles={useStyles}
                                fieldSuggestedResponses={
                                  fieldSuggestedResponses
                                }
                                theme={theme}
                              />
                            ) : (
                              ''
                            )}
                            {section.actions ? (
                              <CardActions
                                className={classes.formSectionActions}
                              >
                                {section.actions.map(
                                  (action: DynamicCardAction) => {
                                    return action.submitFieldAsUtterance ? (
                                      <Button
                                        key="submit"
                                        disabled={
                                          sending ||
                                          !formData[
                                            action.submitFieldAsUtterance
                                          ]
                                        }
                                        size={action.size}
                                        color={action.color}
                                        variant={action.variant || 'secondary'}
                                        startIcon={
                                          getButtonIcon(action.startIcon) ||
                                          undefined
                                        }
                                        endIcon={
                                          getButtonIcon(action.endIcon) ||
                                          undefined
                                        }
                                        onClick={() =>
                                          actionButtonHandler(action)
                                        }
                                      >
                                        {action.label}
                                      </Button>
                                    ) : (
                                      <Button
                                        key="submit"
                                        size={action.size}
                                        color={action.color}
                                        variant={action.variant || 'secondary'}
                                        disabled={sending}
                                        startIcon={
                                          getButtonIcon(action.startIcon) ||
                                          undefined
                                        }
                                        endIcon={
                                          getButtonIcon(action.endIcon) ||
                                          undefined
                                        }
                                        onClick={() =>
                                          actionButtonHandler(action)
                                        }
                                      >
                                        {action.label}
                                      </Button>
                                    )
                                  }
                                )}
                              </CardActions>
                            ) : (
                              ''
                            )}
                          </FormControl>
                        )}
                      </Paper>
                    )
                  })}
                </main>
              ) : (
                <Paper elevation={0} className={classes.loading}>
                  <CircularProgress />
                </Paper>
              )}
            </React.Fragment>
          )}
        </DynamicFormContext.Consumer>
      </ThemeProvider>
    </React.Fragment>
  )
}

export default DynamicForm
